import { Fragment, forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createFloatField, type FloatField } from '@/lib/float-field';
import LIBRARY from '@/lib/overlay-library.json';
import { amountMult, FRAME, placeBox, placeIsNone, placeTurn, type Place } from '@/lib/float-scene.mjs';
import type { DepthPreview } from '@/lib/quicktools-api';
import { cn } from '@/lib/utils';

// A LIVING COVER ON THE PAGE (owner 2026-10-17): "the living covers are
// suppose to be somewhat interactive ... when a user presses or clicks on
// them or brings their cursor over them they should get slightly brighter,
// maybe the floating material could seemingly be disturbed by the air of the
// movement, and if they are paired with a song it should pulsate or vibrate
// in accordance with heavy bass beat".
//
// The video is the cover with nothing floating in it (cover.mp4); what
// floats is drawn live over it (lib/float-field) so the cursor's air and a
// finger's tap move it. The bass: the hits the server found, on the video's
// own clock (so it pulses even muted), and the live bass from Web Audio once
// the sound is on. A hit swells the light (a glow layer drawn from the
// video's own highlights), nudges the picture and shivers the floating things.
//
// With no video, `picture` shows a still (the picker's live preview) and with
// neither the box is black: the floating-kind picker's tiles.
//
// The library's clips (overlay-library.json: the owner's own and the ones he
// picked, 2026-10-20) play whole over the picture, laid on as the downloads lay
// them; a live backdrop plays behind the cut-out subject in place of the
// picture's own background, as canvas.js picturePass draws it.

type LibraryClip = { file: string; blend?: string };
const LIB_OVERLAYS = LIBRARY.overlays as Record<string, LibraryClip>;
const LIB_BACKDROPS = LIBRARY.backdrops as Record<string, LibraryClip>;
/**
 * The clip an overlay plays: the library's overlay, or a backdrop clip laid on as one (light, screen), as the file lays
 * it (particles.js clipOf; owner 2026-10-27, his police lights).
 */
const OVERLAYS = new Proxy({} as Record<string, LibraryClip>, {
  get: (_, k: string) => LIB_OVERLAYS[k] || (LIB_BACKDROPS[k] ? { file: LIB_BACKDROPS[k].file, blend: 'screen' } : undefined),
});
const BLEND: Record<string, CSSProperties['mixBlendMode']> = { screen: 'screen', overlay: 'overlay', multiply: 'multiply' };
/**
 * How strong the amount slider and the clip's own see-through slider (opacity, 1 solid) make a whole clip, as
 * canvas.js floatPass lays it on: its light scaled (brightness multiplies, as the file's lutrgb), or a texture laid
 * on less.
 */
function clipStrength(blend: string, amount?: string, opacity = 1): CSSProperties {
  const gain = amountMult(amount ?? '50');
  if (blend === 'screen') {
    const k = gain * opacity;
    return Math.abs(k - 1) < 0.01 ? {} : { filter: `brightness(${k.toFixed(3)})` };
  }
  const lay = Math.min(1, gain) * opacity;
  return lay < 0.99 ? { opacity: lay } : {};
}

/**
 * A library clip moved or resized (owner 2026-10-24, InShot's overlays): its box where canvas.js clipPlace puts it in the
 * file (placeBox, shares of the frame) and its edges inside the frame faded to nothing over the same widths.
 */
function placedClip(p: Place, frame: { w: number; h: number }): CSSProperties {
  const b = placeBox(p, frame);
  const turn = placeTurn(p);
  const fade = (to: string, a: number, c: number, len: number) =>
    `linear-gradient(to ${to}, ${a > 0 ? 'transparent' : '#000'} 0%, #000 ${((a / len) * 100).toFixed(3)}%, #000 ${(100 - (c / len) * 100).toFixed(3)}%, ${c > 0 ? 'transparent' : '#000'} 100%)`;
  const mask = `${fade('right', b.fl, b.fr, b.w)}, ${fade('bottom', b.ft, b.fb, b.h)}`;
  return {
    left: `${((b.left / frame.w) * 100).toFixed(4)}%`,
    top: `${((b.top / frame.h) * 100).toFixed(4)}%`,
    width: `${(p.s * 100).toFixed(4)}%`,
    height: `${(p.s * 100).toFixed(4)}%`,
    WebkitMaskImage: mask,
    // The two fades multiplied (older Safari's word first, the standard one last so it wins where both are known).
    WebkitMaskComposite: 'source-in',
    maskImage: mask,
    maskComposite: 'intersect',
    // Turned about its middle (owner 2026-10-27), as canvas.js clipPlaceTurned turns it.
    ...(turn ? { transform: `rotate(${turn}deg)` } : {}),
  } as CSSProperties;
}

export type BackdropLayer = { src: string; place?: Place | null; opacity?: number };

/** The backdrops behind the subject (canvas.js drawBackdrops): the plate under them unless one solid one covers it all. */
export function BackdropStack({ layers, plate, frame, filter }: { layers: BackdropLayer[]; plate?: string; frame: { w: number; h: number }; filter: string }) {
  const plain = layers.length === 1 && placeIsNone(layers[0].place) && !((layers[0].opacity ?? 1) < 0.995);
  return (
    <>
      {!plain && plate && <img src={plate} alt="" aria-hidden className="absolute inset-0 h-full w-full" style={{ filter }} />}
      {layers.map((l, i) => {
        const placed = !placeIsNone(l.place) && l.place;
        const see = (l.opacity ?? 1) < 0.995 ? { opacity: l.opacity } : {};
        return (
          <video key={`${l.src}-${i}`} src={l.src} autoPlay muted loop playsInline aria-hidden data-testid="lc-backdrop"
            className={cn('absolute object-cover', !placed && 'inset-0 h-full w-full')}
            style={{ ...(placed ? placedClip(placed, frame) : {}), ...see, filter }} />
        );
      })}
    </>
  );
}

const GLOW_W = 135;
const GLOW_H = 240;

export interface LivingCoverHandle {
  /** Sound on, through the bass analyser. Call inside a tap (phones only start audio from a gesture). */
  startSound: () => void;
  video: HTMLVideoElement | null;
}

export interface LivingCoverProps {
  video?: string;
  poster?: string;
  picture?: string;
  /** CSS filter for the still picture (the live preview's black and white). */
  pictureFilter?: string;
  kinds: string[];
  amount?: string;
  /** The glow slider (0..100 or the old words): the preview's glow follows it as the download's halo does. */
  glow?: string;
  /**
   * The brightness bar (0..100, 50 as made; owner 2026-10-25). The file lifts its light by a curve (canvas.js
   * brightCurve); the preview by the brightness and saturation that give the middle greys the same lift (brightVars).
   */
  brightness?: string;
  /** The picture's depth (the server's cutout of it): the subject leans in and breathes apart from the background, as in the file. */
  depth?: DepthPreview | null;
  /** Each overlay's own see-through slider ({ kind: 0..1 }; absent: solid), as the file lays it on. */
  opacity?: Record<string, number> | null;
  /** Where each overlay sits ({ kind: { x, y, s } }, float-scene placeBox; absent: the whole frame), as the file places it. */
  spot?: Record<string, Place> | null;
  /** A still object far away (owner 2026-10-23): it leans in less, as in the file (canvas.js DEPTH.leanFar). */
  far?: boolean;
  /** The customer removed their photo's background: the subject alone, on black until they pick a backdrop. Needs `depth`. */
  cutOut?: boolean;
  /** A live backdrop's loop (overlay-library.json backdrops): behind the subject, in place of its background. Needs `depth`. */
  backdrop?: string | null;
  /**
   * Several live backdrops, bottom first, each moved, resized and see-through (owner 2026-10-27), as canvas.js
   * drawBackdrops lays them: the picture's own background shows wherever none covers it. Wins over `backdrop`.
   */
  backdrops?: BackdropLayer[] | null;
  /**
   * The new motion as the cover's own engine drew it (canvas.js motionPreview: the moving sky, the float, the wind, the
   * painted water and flags; a small loop, ungraded): shown in place of the still and its CSS depth, graded and lit
   * the same way (owner 2026-10-23: the page's preview is the only one before paying).
   */
  motion?: string | null;
  /**
   * A new sky while the engine's preview of it is on its way (browsing skies must be instant): the sky clip behind,
   * the picture over it cut to what stays in front (`front`: white, its alpha the front, sky.py's).
   */
  skyLive?: { front: string; clip: string } | null;
  /** The heavy kicks: flash and thump. */
  beats?: number[];
  /** The other bass hits: a shake, softer for a softer hit (s 0.4..1). */
  shakes?: { t: number; s: number }[];
  /**
   * The bass shake on the still preview while the song plays (owner 2026-10-27: "that way they can choose the section
   * with the bass effect they want and see it throughout the creation instead of at the very end"): `level` the song
   * part's bass loudness every 10 ms (canvas.js bassLevel, the same numbers), `at()` seconds into the part now, or null
   * while the song is not playing.
   */
  bass?: { level: Float32Array; at: () => number | null } | null;
  /** How dark the picture is, 0..1 (canvas.js DARK): the grade, the breath, the glow and the flash ease as in the file. */
  dark?: number;
  /** The frame's size when it is not 1080x1920 (owner 2026-10-23: the shape picked); the box's own aspect is the caller's. */
  frame?: { w: number; h: number } | null;
  /** The small picker tile: no picture, fewer and larger things. */
  preview?: boolean;
  /** Pause when not on screen and skip the bass work (the picker's many tiles). */
  light?: boolean;
  className?: string;
  style?: CSSProperties;
  onTap?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  children?: ReactNode;
  autoPlay?: boolean;
  label?: string;
}

// canvas.js glowLayers' curves, sampled at 0, 0.1 .. 1: colour '0/0 0.35/0.22 0.7/0.62 1/1', black and white
// '0/0 0.35/0.1 0.7/0.55 1/1'.
const curve = (pts: [number, number][]) => Array.from({ length: 11 }, (_, i) => {
  const x = i / 10;
  const k = pts.findIndex(([px]) => px >= x);
  if (k <= 0) return pts[0][1];
  const [x0, y0] = pts[k - 1]; const [x1, y1] = pts[k];
  return +(y0 + ((x - x0) * (y1 - y0)) / (x1 - x0)).toFixed(3);
}).join(' ');
// canvas.js glowGain: the slider's 0..100 (or the old words) as the halo's gain; 2.6 is Normal.
const GLOW_WORDS: Record<string, number> = { soft: 1.8, normal: 2.6, strong: 3.4 };
const glowGain = (g?: string) => (g && GLOW_WORDS[g] ? GLOW_WORDS[g] : g !== undefined && g !== '' && Number.isFinite(Number(g)) ? 1 + Math.max(0, Math.min(100, Number(g))) * 0.032 : 2.6);
// A dark picture (canvas.js DARK, owner 2026-10-24: "look how dark it is"): the curve's darkening eased back, the
// breath's low point raised, the glow started on dimmer light and the flash lifted, the same numbers as the file.
const DARK = { gradeMid: 0.13, gradeHigh: 0.08, floor: 0.3, glowFrom: 50, flash: 0.8 };
const gradeColor = (d = 0) => curve([[0, 0], [0.35, 0.22 + DARK.gradeMid * d], [0.7, 0.62 + DARK.gradeHigh * d], [1, 1]]);
const gradeBw = (d = 0) => curve([[0, 0], [0.35, 0.1 + 0.25 * d], [0.7, 0.55 + 0.15 * d], [1, 1]]);
const breathFloor = (d = 0) => 0.5 + DARK.floor * d;
// The bass wobble's strength (canvas.js HIT.wave: 6 px of a 1080-wide frame at its peak).
const WAVE = 6 / 1080;
/** A layer cut to a mask (a data URL: white with the mask as its alpha), stretched over the 9:16 box like the file's. */
const masked = (url: string): CSSProperties => ({
  maskImage: `url("${url}")`,
  WebkitMaskImage: `url("${url}")`,
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
});
/**
 * The subject cut out, as canvas.js depthSubject draws it: the picture under its mask and, when it breathes, a copy
 * rising above the shoulders (full above ys, nothing below yc) laid over it.
 */
function DepthSubject({ src, mask, filter, breath }: { src: string; mask: string; filter: string; breath?: { ys: number; yc: number } | null }) {
  const img = <img src={src} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter, ...masked(mask) }} />;
  if (!breath) return img;
  const fade = `linear-gradient(to bottom, #000 ${(breath.ys * 100).toFixed(2)}%, transparent ${(breath.yc * 100).toFixed(2)}%)`;
  return (
    <>
      {img}
      <div className="lc-depth-breath absolute inset-0" style={{ maskImage: fade, WebkitMaskImage: fade }}>
        {img}
      </div>
    </>
  );
}

/** A new sky behind the picture, the picture cut to what stays in front of it (sky.py's front). */
function SkyLive({ sky, picture, filter }: { sky: { front: string; clip: string }; picture: string; filter: string }) {
  return (
    <>
      <video src={sky.clip} autoPlay muted loop playsInline aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter }} data-testid="lc-sky-live" />
      <img src={picture} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter, ...masked(sky.front) }} />
    </>
  );
}

/**
 * The brightness bar as CSS (canvas.js brightCurve: out = 1 - (1 - in) ^ k, colour lifted by sqrt k): the brightness
 * that gives the middle greys the file's lift (1 at 50, 1.40 at 100, 0.66 at 0) and the same saturation.
 */
function brightVars(v?: string): CSSProperties {
  const n = Math.max(0, Math.min(100, Number(v ?? 50)));
  if (!Number.isFinite(n) || Math.round(n) === 50) return {};
  const k = 2 ** ((0.8 * (n - 50)) / 50);
  return { '--lc-b': ((1 - 0.5 ** k) / 0.5).toFixed(3), '--lc-s': Math.sqrt(k).toFixed(3) } as CSSProperties;
}

const VIGNETTE = `radial-gradient(farthest-corner at 50% 50%, ${[0, 0.3, 0.55, 0.75, 0.9, 1]
  .map((r) => `rgba(0,0,0,${(1 - Math.cos((Math.PI / 4.5) * r) ** 4).toFixed(3)}) ${Math.round(r * 100)}%`).join(', ')})`;

export const LivingCover = forwardRef<LivingCoverHandle, LivingCoverProps>(function LivingCover(
  { video, poster, picture, pictureFilter, kinds, amount, opacity, spot, glow, brightness, depth, far = false, cutOut, backdrop: oneBackdrop, backdrops: backdropList, motion, skyLive, beats, shakes, bass, dark = 0, preview, light, frame, className, style, onTap, children, autoPlay = true, label },
  ref,
) {
  const boxRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const floatRef = useRef<HTMLCanvasElement>(null);
  const glowRef = useRef<HTMLCanvasElement>(null);
  // Each cover's light filter has its own id: two covers on a page can have different glow strengths.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const glowId = `lc-glow-${uid}`;
  // The bass wobble's filter (a slight vibrating distortion, owner 2026-10-24), its strength set frame by frame.
  const quakeId = `lc-quake-${uid}`;
  const quakeRef = useRef<SVGFEDisplacementMapElement>(null);
  const GRADE_COLOR = gradeColor(dark);
  const GRADE_BW = gradeBw(dark);
  const floor = breathFloor(dark);
  const hitRef = useRef<HTMLDivElement>(null);
  const field = useRef<FloatField | null>(null);
  const audio = useRef<{ ctx: AudioContext; analyser: AnalyserNode; bins: Uint8Array; lo: number; hi: number } | null>(null);
  const kick = useRef({ flash: 0, mean: 0, lastBeat: -1 });
  const lastPt = useRef<{ x: number; y: number } | null>(null);
  const [pressed, setPressed] = useState(false);
  const kindsKey = kinds.join(',');
  // The see-through sliders move live: the drawn things stay where they are, only fainter or stronger.
  const backdropLayers: BackdropLayer[] = backdropList?.length ? backdropList : oneBackdrop ? [{ src: oneBackdrop }] : [];
  const backdrop = backdropLayers.length > 0;
  const opacityRef = useRef(opacity);
  opacityRef.current = opacity;
  const opacityKey = JSON.stringify(opacity || {});
  useEffect(() => {
    field.current?.setOpacity(opacity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opacityKey]);
  // Where each overlay sits moves live too (dragged on the preview).
  const spotRef = useRef(spot);
  spotRef.current = spot;
  const spotKey = JSON.stringify(spot || {});
  useEffect(() => {
    field.current?.setPlace(spot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotKey]);

  // The floating things, live.
  useEffect(() => {
    const canvas = floatRef.current;
    if (!canvas || !kinds.length) return undefined;
    const f = createFloatField(canvas, { kinds, amount, preview, lightFrom: picture || poster, opacity: opacityRef.current, place: spotRef.current, frame });
    field.current = f;
    return () => {
      f.stop();
      field.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindsKey, amount, preview, picture, poster, frame?.w, frame?.h]);

  // The bass: swell the light, nudge the picture, shiver the floating things.
  useEffect(() => {
    if (light || !video) return undefined;
    const v = videoRef.current;
    const canvas = glowRef.current;
    const stage = stageRef.current;
    const g = canvas?.getContext('2d', { alpha: false });
    if (!v || !canvas || !stage || !g) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // The heavy hits (the server sends only those since 2026-10-17); older covers listed every hit, so keep them 0.3 s apart.
    const hits: number[] = [];
    for (const b of beats || []) if (!hits.length || b - hits[hits.length - 1] >= 0.3) hits.push(b);
    const hitEl = hitRef.current;
    let raf = 0;
    const floats = floatRef.current;
    // Speed (owner 2026-10-20: "the site is extremely laggy"): nothing is worked out for a cover off screen, styles are
    // only written when they change, and the beat's light layer is only drawn while it is lit.
    let onScreen = true;
    const io = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; }) : null;
    if (boxRef.current) io?.observe(boxRef.current);
    let lastBright = '';
    let lastHit = '';
    let lit = true;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      if (!onScreen || v.paused || v.readyState < 2) return;
      const k = kick.current;
      const now = v.currentTime;
      // The floating things breathe with the picture (owner 2026-10-17: the pulse "should also pertain equally to
      // the overlay material"): the same swell the server put in the video, on its own clock (one per 7.5 s loop).
      if (floats && !reduce) {
        const shape = Math.pow(0.5 - 0.5 * Math.cos((2 * Math.PI * (now % 7.5)) / 7.5), 1.4);
        const bright = `brightness(${(floor + (1.05 - floor) * shape).toFixed(2)})`;
        if (bright !== lastBright) floats.style.filter = lastBright = bright;
      }
      let level = 0;
      let latest = -1;
      // The hit itself, the same numbers as the downloads (canvas.js HIT): a heavy kick flashes +35% and thumps
      // in 3.5%; every other bass hit only shakes, ~9 px at full strength (owner 2026-10-18: "only include the
      // thump for when its a heavy bass kick and than vibration for normal ones").
      let thump = 0;
      let sx = 0;
      let sy = 0;
      let wave = 0;
      for (const b of hits) {
        if (b > now) break;
        latest = b;
        const dt = now - b;
        if (dt < 1) level = Math.max(level, Math.exp(-dt / 0.2));
        if (dt < 0.8) {
          thump = Math.max(thump, Math.exp(-dt / 0.13));
          // The kick vibrates too (canvas.js HIT.vibrate, 7 px of the shake's 9) and wobbles the picture (HIT.wave).
          const v = Math.exp(-dt / 0.16);
          sx += 0.78 * v * Math.sin(dt * 94 + 0.6);
          sy += 0.78 * v * Math.cos(dt * 77);
          wave = Math.max(wave, Math.exp(-dt / 0.2));
        }
      }
      for (const h of shakes || []) {
        if (h.t > now) break;
        const dt = now - h.t;
        if (dt < 0.45) {
          const e = h.s * Math.exp(-dt / 0.09);
          sx += e * Math.sin(dt * 88);
          sy += e * Math.cos(dt * 71);
          wave = Math.max(wave, 0.6 * h.s * Math.exp(-dt / 0.135));
        }
      }
      if (hitEl && (hits.length || shakes?.length) && !reduce) {
        const tf = `translate(${(sx * 0.83).toFixed(3)}%, ${(sy * 0.28).toFixed(3)}%) scale(${(1.015 + 0.035 * Math.min(1, thump)).toFixed(4)})`;
        const fl = thump > 0.01 ? `brightness(${(1 + (0.35 + DARK.flash * dark) * thump).toFixed(3)})` : '';
        // The wobble flips every frame while it lasts (a vibration, not a sway); off when it has faded.
        const q = quakeRef.current;
        const on = Boolean(q) && wave > 0.03;
        if (q && on) q.setAttribute('scale', ((Math.round(now * 30) % 2 ? -1 : 1) * 2 * WAVE * hitEl.clientWidth * wave).toFixed(2));
        const filt = `${fl}${on ? `${fl ? ' ' : ''}url(#${quakeId})` : ''}`;
        if (tf + filt !== lastHit) {
          hitEl.style.transform = tf;
          hitEl.style.filter = filt;
          lastHit = tf + filt;
        }
      }
      if (latest >= 0 && latest !== k.lastBeat && now - latest < 0.1) field.current?.kick(0.8);
      k.lastBeat = latest;
      const a = audio.current;
      if (a && !v.muted) {
        a.analyser.getByteFrequencyData(a.bins);
        let sum = 0;
        for (let i = a.lo; i <= a.hi; i++) sum += a.bins[i];
        const bass = sum / ((a.hi - a.lo + 1) * 255);
        k.mean = k.mean * 0.95 + bass * 0.05;
        const hit = Math.max(0, bass - k.mean * 1.08) * 6;
        if (hit > 0.5) field.current?.kick(Math.min(1, hit) * 0.6);
        level = Math.max(level, Math.min(1, bass * 0.35 + hit));
      }
      k.flash *= 0.9;
      if (!hits.length && !a) return;
      const glowOn = Math.min(1, level * 0.75 + k.flash);
      if (glowOn < 0.01) {
        if (lit) canvas.style.opacity = '0';
        lit = false;
        return;
      }
      lit = true;
      g.globalCompositeOperation = 'source-over';
      g.drawImage(v, 0, 0, GLOW_W, GLOW_H);
      g.globalCompositeOperation = 'multiply';
      g.drawImage(v, 0, 0, GLOW_W, GLOW_H);
      g.drawImage(v, 0, 0, GLOW_W, GLOW_H);
      canvas.style.opacity = String(glowOn);
      // (the thump and shake are on hitRef above)
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
    };
  }, [video, beats, shakes, light, dark, floor, quakeId]);

  // The bass on the still preview (the editor, while the song plays): the camera shaken by a speaker, as the file
  // (canvas.js motionTrack with the song's bass level): a new jump every frame, back and forth, as hard as the bass is
  // loud, HIT.vibrate (10 px of 1080) at full; and the rows' ripple (HIT.wave) with it. Still when the song stops.
  const bassRef = useRef(bass);
  bassRef.current = bass;
  const bassOn = Boolean(bass);
  useEffect(() => {
    if (video || !bassOn) return undefined;
    const hitEl = hitRef.current;
    if (!hitEl) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const jit = (n: number, k: number) => { const v = Math.sin(n * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); };
    let raf = 0;
    let last = '';
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const b = bassRef.current;
      const t = b ? b.at() : null;
      const e = b && t !== null && b.level.length ? b.level[Math.max(0, Math.min(b.level.length - 1, Math.round(t / 0.01)))] : 0;
      const n = Math.round((t ?? 0) * 30);
      const sign = n % 2 ? -1 : 1;
      const sx = (10 / 1080) * 100 * e * sign * (0.55 + 0.45 * jit(n, 1));
      const sy = (7 / 1920) * 100 * e * -sign * (0.25 + 0.75 * jit(n, 2)) * (jit(n, 3) < 0.5 ? 1 : -1);
      const tf = e > 0.01 ? `translate(${sx.toFixed(3)}%, ${sy.toFixed(3)}%) scale(1.015)` : '';
      const q = quakeRef.current;
      if (q) q.setAttribute('scale', e > 0.03 ? (sign * 2 * WAVE * 0.5 * hitEl.clientWidth * e).toFixed(2) : '0');
      const filt = e > 0.03 && q ? `url(#${quakeId})` : '';
      if (tf + filt !== last) {
        hitEl.style.transform = tf;
        hitEl.style.filter = filt;
        last = tf + filt;
      }
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      hitEl.style.transform = '';
      hitEl.style.filter = '';
    };
  }, [video, bassOn, quakeId]);

  // Off screen or in a background tab: stop the video.
  useEffect(() => {
    const v = videoRef.current;
    const box = boxRef.current;
    if (!v || !box || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && autoPlay) void v.play().catch(() => undefined);
      else v.pause();
    });
    io.observe(box);
    return () => io.disconnect();
  }, [video, autoPlay]);

  const startSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    if (!audio.current) {
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.3;
        ctx.createMediaElementSource(v).connect(analyser);
        analyser.connect(ctx.destination);
        const hz = ctx.sampleRate / analyser.fftSize;
        audio.current = { ctx, analyser, bins: new Uint8Array(analyser.frequencyBinCount), lo: Math.max(1, Math.floor(35 / hz)), hi: Math.ceil(150 / hz) };
      } catch {
        /* no Web Audio: the sound still plays and the beat clock keeps the pulse going */
      }
    }
    void audio.current?.ctx.resume();
    void v.play().catch(() => undefined);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      startSound,
      get video() {
        return videoRef.current;
      },
    }),
    [startSound],
  );

  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = lastPt.current;
    const mx = e.movementX || (p ? e.clientX - p.x : 0);
    const my = e.movementY || (p ? e.clientY - p.y : 0);
    lastPt.current = { x: e.clientX, y: e.clientY };
    field.current?.stir(e.clientX, e.clientY, mx, my);
  };

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    setPressed(true);
    lastPt.current = { x: e.clientX, y: e.clientY };
    field.current?.puff(e.clientX, e.clientY, 1);
    kick.current.flash = 0.5;
    onTap?.(e);
  };

  const release = () => setPressed(false);

  return (
    <div
      ref={boxRef}
      className={cn('group/lc relative isolate overflow-hidden bg-black', className)}
      style={{ touchAction: 'pan-y', ...style }}
      onPointerMove={onMove}
      onPointerDown={onDown}
      onPointerUp={release}
      onPointerLeave={() => {
        release();
        lastPt.current = null;
      }}
      onPointerCancel={release}
      role={label ? 'img' : undefined}
      aria-label={label}
      data-testid="living-cover-box"
    >
      {/* Slightly brighter under the cursor, brighter still while pressed. */}
      <div
        ref={stageRef}
        className={cn(
          'absolute inset-0 transition-[filter] duration-300 will-change-transform',
          pressed
            ? '[filter:brightness(calc(var(--lc-b,1)*1.22))_saturate(var(--lc-s,1))]'
            : '[filter:brightness(var(--lc-b,1))_saturate(var(--lc-s,1))] group-hover/lc:[filter:brightness(calc(var(--lc-b,1)*1.12))_saturate(var(--lc-s,1))]',
        )}
        style={brightVars(brightness)}
      >
        <div ref={hitRef} className="absolute inset-0 will-change-transform">
        {(video || bass) && (
          <svg aria-hidden width="0" height="0" className="absolute">
            <filter id={quakeId} x="-2%" y="-2%" width="104%" height="104%" colorInterpolationFilters="sRGB">
              <feTurbulence type="fractalNoise" baseFrequency="0.004 0.011" numOctaves={1} seed={3} result="n" />
              <feDisplacementMap ref={quakeRef} in="SourceGraphic" in2="n" scale={0} xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </svg>
        )}
        {video ? (
          <video ref={videoRef} src={video} poster={poster} autoPlay={autoPlay} muted loop playsInline preload={light ? 'metadata' : 'auto'} aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        ) : picture ? (
          // Remounted when the depth arrives, so its motion and the light's breath start on the same beat.
          <div key={motion ? 'motion' : depth?.depth ? 'depth' : 'flat'} className="living-dim absolute inset-0" style={{ '--lc-floor': floor } as CSSProperties}>
            {/* The download's own grade (owner 2026-10-20: "it needs to look the same as it is showing you its going to
                look like while your creating it... not a suprise finish"): canvas.js glowLayers' saturation and tone
                curve, the same numbers, as an SVG filter. */}
            <svg aria-hidden width="0" height="0" className="absolute">
              <filter id="lc-grade-color" colorInterpolationFilters="sRGB">
                <feColorMatrix type="saturate" values="1.2" />
                <feComponentTransfer>
                  {(['R', 'G', 'B'] as const).map((c) => {
                    const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                    return <F key={c} type="table" tableValues={GRADE_COLOR} />;
                  })}
                </feComponentTransfer>
              </filter>
              {/* The download's light layer, the same steps (canvas.js glowLayers, owner 2026-10-20 picked "C"): the
                  graded picture in gray, only what is lighter than 150 of 255 kept, then a tight halo, a wide halo
                  and a crisp core screened together, at the breath's peak gain (1.55). Blur sizes are shares of the
                  frame (9 and 26 px of 810x1440) so any preview size matches the file. */}
              <filter id={glowId} colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%" primitiveUnits="objectBoundingBox">
                <feColorMatrix type="saturate" values={pictureFilter ? '0.08' : '1.2'} />
                <feComponentTransfer>
                  {(['R', 'G', 'B'] as const).map((c) => {
                    const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                    return <F key={c} type="table" tableValues={pictureFilter ? GRADE_BW : GRADE_COLOR} />;
                  })}
                </feComponentTransfer>
                <feColorMatrix type="matrix" values="0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0 0 0 0 1" />
                <feComponentTransfer result="hl">
                  {(['R', 'G', 'B'] as const).map((c) => {
                    const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                    return <F key={c} type="linear" slope={1.7} intercept={(-1.7 * (150 - DARK.glowFrom * Math.max(0, 2 * dark - 1))) / 255} />;
                  })}
                </feComponentTransfer>
                {([['hn', 9, 1.5, 4], ['hw', 26, 1.3, 2]] as const).map(([id, px, mult, cut]) => {
                  const k = glowGain(glow) * mult;
                  return (
                    <Fragment key={id}>
                      <feGaussianBlur in="hl" stdDeviation={`${(px / 810).toFixed(5)} ${(px / 1440).toFixed(5)}`} />
                      <feComponentTransfer result={id}>
                        {(['R', 'G', 'B'] as const).map((c) => {
                          const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                          return <F key={c} type="linear" slope={k} intercept={(-cut / 255) * k} />;
                        })}
                      </feComponentTransfer>
                    </Fragment>
                  );
                })}
                <feGaussianBlur in="hl" stdDeviation={`${(1.4 / 810).toFixed(5)} ${(1.4 / 1440).toFixed(5)}`} />
                <feComponentTransfer result="core">
                  {(['R', 'G', 'B'] as const).map((c) => {
                    const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                    return <F key={c} type="linear" slope={0.9} />;
                  })}
                </feComponentTransfer>
                <feBlend in="hn" in2="hw" mode="screen" result="halo" />
                <feBlend in="halo" in2="core" mode="screen" />
                <feComponentTransfer>
                  {(['R', 'G', 'B'] as const).map((c) => {
                    const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                    return <F key={c} type="linear" slope={1.55} />;
                  })}
                </feComponentTransfer>
              </filter>
              <filter id="lc-grade-bw" colorInterpolationFilters="sRGB">
                <feColorMatrix type="saturate" values="0.08" />
                <feComponentTransfer>
                  {(['R', 'G', 'B'] as const).map((c) => {
                    const F = c === 'R' ? 'feFuncR' : c === 'G' ? 'feFuncG' : 'feFuncB';
                    return <F key={c} type="table" tableValues={GRADE_BW} />;
                  })}
                </feComponentTransfer>
              </filter>
            </svg>
            {motion ? (
              // The engine's own loop of the new motion, graded as the file is.
              <video src={motion} autoPlay muted loop playsInline aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter: pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)' }} data-testid="lc-motion" />
            ) : depth?.depth && depth.mask && depth.inv && depth.halo && depth.plate ? (
              <>
                {/* THE DEPTH (canvas.js picturePass with depth): the background (the plate, the subject's place filled)
                    takes the drift with its zoom easing back; the subject, cut out, leans toward you about its feet
                    and breathes. */}
                <div className="lc-depth-bg absolute inset-0">
                  {backdrop ? (
                    // The live backdrops in place of the plate, with the picture's grade (canvas.js drawBackdrops).
                    <BackdropStack layers={backdropLayers} plate={depth.plate} frame={frame || FRAME} filter={pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)'} />
                  ) : cutOut ? null : skyLive ? (
                    <SkyLive sky={skyLive} picture={depth.plate} filter={pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)'} />
                  ) : (
                    <img src={depth.plate} alt="" aria-hidden className="absolute inset-0 h-full w-full" style={{ filter: pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)' }} />
                  )}
                </div>
                <div className="lc-depth-bg absolute inset-0">
                  <div className={cn('lc-depth-lean absolute inset-0', far && 'lc-far')} style={{ transformOrigin: `${((depth.ax ?? 0.5) * 100).toFixed(2)}% ${((depth.ay ?? 1) * 100).toFixed(2)}%` }}>
                    <DepthSubject src={picture} mask={depth.mask} filter={pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)'} breath={depth.breath} />
                  </div>
                </div>
              </>
            ) : skyLive ? (
              <div className="living-sway absolute inset-0">
                <SkyLive sky={skyLive} picture={picture} filter={pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)'} />
              </div>
            ) : (
              <img src={picture} alt="" aria-hidden className="living-sway absolute inset-0 h-full w-full object-cover" style={{ filter: pictureFilter ? 'url(#lc-grade-bw)' : 'url(#lc-grade-color)' }} />
            )}
            {/* The breathing light, roughly: the picture's own highlights, blurred and screened over it. A blur fades
                to nothing at its own edge, which showed as a dark strip and a line inside the frame (owner 2026-10-18),
                so the blurred layer is drawn a little larger than the frame and its faded edge falls outside. */}
            {/* The light (owner 2026-10-20 picked "C": only the light parts glow, no outlines): the file's own light
                layer (#lc-glow), screened over the picture, swelling with the breath. Only highlights are kept, so
                nothing fades in at the frame's edge and it sits exactly on the picture. */}
            <div className="pointer-events-none absolute inset-0" style={{ mixBlendMode: 'screen' }}>
              {motion ? (
                // The engine's loop lit by the same light filter: the glow stays on what moves.
                <div className="lc-glow-breath absolute inset-0">
                  <video src={motion} autoPlay muted loop playsInline aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter: `url(#${glowId})` }} />
                </div>
              ) : depth?.depth && depth.mask && depth.inv && depth.halo ? (
                // With depth (canvas.js lightPass): the light outside the subject stays with the background; the
                // subject's light, with the glow around it, leans and breathes with the subject.
                <div className="lc-glow-breath absolute inset-0">
                  {/* With a live backdrop the background's own light went with it (canvas.js lightPass): only the subject's. */}
                  {!backdrop && !cutOut && (
                    <div className="lc-depth-bg absolute inset-0">
                      <img src={picture} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" style={{ filter: `url(#${glowId})`, ...masked(depth.inv) }} />
                    </div>
                  )}
                  <div className="lc-depth-bg absolute inset-0">
                    <div className={cn('lc-depth-lean absolute inset-0', far && 'lc-far')} style={{ transformOrigin: `${((depth.ax ?? 0.5) * 100).toFixed(2)}% ${((depth.ay ?? 1) * 100).toFixed(2)}%` }}>
                      {/* With a backdrop (or the background removed) only the subject's own light, to its outline: the grown
                          halo carried the old room's lamps and a white rim onto the backdrop (canvas.js, the same). */}
                      <DepthSubject src={picture} mask={backdrop || cutOut ? depth.mask : depth.halo} filter={`url(#${glowId})`} breath={depth.breath} />
                    </div>
                  </div>
                </div>
              ) : (
                <img
                  src={picture}
                  alt=""
                  aria-hidden
                  className="living-sway living-breath absolute inset-0 h-full w-full object-cover"
                  style={{ filter: `url(#${glowId})` }}
                />
              )}
            </div>
            {/* The download's vignette (glowLayers: vignette=angle=PI/4.5), the same falloff: 1 - cos(angle * r)^4. */}
            <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: VIGNETTE }} />
            {/* The file's blacks sit a little above black (its light layer's black is video black, 16 of 255, screened
                over the picture: measured 14-16 at the breath's peak, 5-8 at its low). The same lift, swelling with
                the breath like the file's. */}
            <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'rgb(14,15,13)', mixBlendMode: 'screen' }} />
          </div>
        ) : null}
        {video && !light && (
          <canvas
            ref={glowRef}
            width={GLOW_W}
            height={GLOW_H}
            aria-hidden
            // Larger than the frame: the blur's faded edge stays outside it (see the picture glow above).
            className="pointer-events-none absolute inset-0 h-full w-full scale-[1.09] object-cover"
            style={{ mixBlendMode: 'screen', filter: 'blur(14px) brightness(1.7) saturate(1.3)', opacity: 0 }}
          />
        )}
        {/* The library's clips: played whole over the picture, as the downloads lay them on. */}
        {/* A picker tile has no picture: a texture laid on grey or white gets a dark city-night backdrop, so it reads as it will on a cover. */}
        {!picture && !video && kinds.some((k) => OVERLAYS[k] && OVERLAYS[k].blend !== 'screen') && (
          <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_70%,#3a4a66_0%,#1b2333_45%,#0b0f17_100%)]" />
        )}
        {kinds.filter((k) => OVERLAYS[k]).map((k) => (spot && !placeIsNone(spot[k]) ? (
          // Moved or resized: the clip in its own box, laid on (the blend, the sliders) from there, so its edges fade.
          <div
            key={k}
            aria-hidden
            className="pointer-events-none absolute"
            style={{ ...placedClip(spot[k], frame || FRAME), mixBlendMode: BLEND[OVERLAYS[k].blend || 'screen'] || 'screen', ...clipStrength(OVERLAYS[k].blend || 'screen', amount, opacity?.[k] ?? 1) }}
            data-testid={`lc-placed-${k}`}
          >
            <video src={OVERLAYS[k].file} autoPlay muted loop playsInline preload={light ? 'metadata' : 'auto'} className="absolute inset-0 h-full w-full object-cover" />
          </div>
        ) : (
          <video
            key={k}
            src={OVERLAYS[k].file}
            autoPlay
            muted
            loop
            playsInline
            preload={light ? 'metadata' : 'auto'}
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            // The amount slider, as the file lays it on (canvas.js floatPass): light scaled, a texture made fainter.
            style={{ mixBlendMode: BLEND[OVERLAYS[k].blend || 'screen'] || 'screen', ...clipStrength(OVERLAYS[k].blend || 'screen', amount, opacity?.[k] ?? 1) }}
          />
        )))}
        {/* A still picture (the live preview) breathes by CSS; a video's float layer follows the video's clock above. */}
        <canvas ref={floatRef} aria-hidden className={cn('pointer-events-none absolute inset-0 h-full w-full', !video && picture && 'living-dim')} style={{ '--lc-floor': floor } as CSSProperties} />
        </div>
      </div>
      {children}
    </div>
  );
});
